import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import Base64Image from '../components/Base64Image';
import {
  ActsPageContainer,
  Section,
  SectionTitle,
  TextBlock,
  TwoColumn,
  GalleryGrid,
  GalleryCard,
  GalleryTitle,
  GalleryMeta,
  GalleryDescription,
} from '../styles/ActsFellowshipPageStyles';

const CACHE_KEY = 'acts_fellowship_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const ActsFellowshipPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/acts-fellowship');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load Acts Fellowship page.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <ActsPageContainer>
        <LoadingSpinner />
      </ActsPageContainer>
    );
  }

  if (error && !pageData) {
    return <ActsPageContainer>{error}</ActsPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const overviewTitle = pageData ? pageData.overviewTitle : null;
  const overviewText = pageData ? pageData.overviewText : null;
  const overviewImage = pageData ? pageData.overviewImage : null;
  const foundedYear = pageData ? pageData.foundedYear : null;
  const visionText = pageData ? pageData.visionText : null;
  const partnershipsText = pageData ? pageData.partnershipsText : null;
  const galleries = pageData && Array.isArray(pageData.galleries) ? pageData.galleries : [];

  const groups = galleries.reduce((acc, item) => {
    const key = item.albumName || item.category || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) => {
      const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
      const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
      return ao - bo;
    });
  });

  const groupKeys = Object.keys(groups);

  return (
    <ActsPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {(overviewTitle || overviewText || foundedYear || overviewImage) && (
        <Section>
          <SectionTitle>{overviewTitle || 'Overview'}</SectionTitle>
          <TwoColumn>
            {overviewImage && (
              <div>
                <Base64Image
                  image={overviewImage}
                  alt={overviewTitle || 'Acts Fellowship overview'}
                  style={{ width: '100%', maxWidth: 420, borderRadius: 8 }}
                />
              </div>
            )}
            <TextBlock>
              {foundedYear && (
                <p><strong>Founded:</strong> {foundedYear}</p>
              )}
              {overviewText && <p>{overviewText}</p>}
            </TextBlock>
          </TwoColumn>
        </Section>
      )}

      {visionText && (
        <Section>
          <SectionTitle>Vision</SectionTitle>
          <TextBlock>{visionText}</TextBlock>
        </Section>
      )}

      {partnershipsText && (
        <Section>
          <SectionTitle>Partnerships</SectionTitle>
          <TextBlock>{partnershipsText}</TextBlock>
        </Section>
      )}

      {groupKeys.map((key) => (
        <Section key={key}>
          <SectionTitle>{key}</SectionTitle>
          <GalleryGrid>
            {groups[key].map((item, index) => (
              <GalleryCard key={item._id || item.title || index}>
                {item.image && (
                  <Base64Image
                    image={item.image}
                    alt={item.title || key}
                    style={{ width: '100%', borderRadius: 6, marginBottom: 6 }}
                  />
                )}
                {item.title && <GalleryTitle>{item.title}</GalleryTitle>}
                {(item.albumName || item.category) && (
                  <GalleryMeta>
                    {item.albumName && <span>Album: {item.albumName}</span>}
                    {item.albumName && item.category && <span> • </span>}
                    {item.category && <span>Category: {item.category}</span>}
                  </GalleryMeta>
                )}
                {item.description && (
                  <GalleryDescription>{item.description}</GalleryDescription>
                )}
              </GalleryCard>
            ))}
          </GalleryGrid>
        </Section>
      ))}
    </ActsPageContainer>
  );
};

export default ActsFellowshipPage;
